import { useSession } from 'next-auth/react';
import { getI18nLinkHref } from '@services/i18nPaths';

/**
 * @description Simple hook to check if the user is authenticated using NextAuth session
 * **isAuth**: boolean - check if user has a valid NextAuth session
 * **isSafeAuth**: boolean - safe auth state to prevent hydration mismatches
 * **redirectToLogin**: function - redirect to login page if not authenticated
 */
export const useAuthClient = () => {
  const { data: session, status } = useSession();

  // Check if the user is authenticated based on NextAuth session
  const isAuth = status === 'authenticated' && !!session;

  // Safe auth state - same as isAuth but can be used to prevent hydration mismatches
  const isSafeAuth = isAuth;

  // Redirect to login page if not authenticated
  const redirectToLogin = (afterLoginRedirectPath?: string) => {
    if (typeof window === 'undefined') return;
    if (!isAuth) {
      let loginUrl = '/login';
      if (afterLoginRedirectPath) {
        loginUrl = `/login?targetPath=${encodeURIComponent(afterLoginRedirectPath)}`;
      }
      const documentLocale = document.documentElement.lang as Locale | '';
      const locale = documentLocale || 'ro';
      window.location.assign(getI18nLinkHref(loginUrl, locale));
    }
  };

  return {
    isAuth,
    isSafeAuth,
    checkAuthCookie: () => isAuth, // Legacy compatibility
    redirectToLogin,
  };
};
